import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { useCollection } from '../runtime/mobile/useCollection';
import { sendTestWorkerCommand, TEST_WORKER_URL } from '../runtime/mobile/testWorkerClient';
import { readableMobileError } from '../runtime/mobile/employeeRuntimeState';
import { colors, radius, spacing, typography } from '../config/theme';
import { Card, ErrorBanner, Field, Loader, Pill, PrimaryButton, SectionLabel, TextButton, screen } from './ui';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const EXPIRY_WARNING_DAYS = 10; // matches the daily Telegram alert threshold
const toMs = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : v instanceof Date ? v.getTime() : Date.parse(v));
const ymd = (ms) => new Date(ms).toISOString().slice(0, 10);
const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

function daysLeftFor(ms) {
  return Math.floor((ms - today0()) / 86400000);
}

function statusFor(daysLeft, warningDays) {
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= (warningDays || 30)) return 'warning';
  return 'ok';
}

const STATUS_PILL = {
  expired: { kind: 'bad', label: 'ПРОСРОЧЕН' },
  warning: { kind: 'warn', label: 'СКОРО' },
  ok: { kind: 'good', label: 'ОК' },
};

const ALL_GROUPS = '__all__';
const leafOf = (folder) => String(folder || '').split('/').pop();

export default function ProductExpiriesScreen() {
  const { user } = useAuth();
  const collection = useCollection('productExpiries');
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState(ALL_GROUPS);
  const [sortMode, setSortMode] = useState('urgency'); // 'urgency' | 'alpha'
  const [modalProduct, setModalProduct] = useState(null); // { id, name }
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const trackedDoc = collection.docs.find((d) => d.id === '_trackedProducts');
  const products = useMemo(() => {
    if (!trackedDoc?.items) return [];
    try { return JSON.parse(trackedDoc.items); } catch { return []; }
  }, [trackedDoc]);

  const groups = useMemo(() => [...new Set(products.map((p) => p.folder).filter(Boolean))].sort(), [products]);

  const records = collection.docs.filter((d) => d.id !== '_trackedProducts' && d.id !== '_config' && d.status === 'active');
  const recordsByProduct = useMemo(() => {
    const map = {};
    for (const r of records) (map[r.productId] ||= []).push(r);
    return map;
  }, [records]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const withEntries = products
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .filter((p) => groupFilter === ALL_GROUPS || p.folder === groupFilter)
      .map((p) => {
        const entries = (recordsByProduct[p.id] || [])
          .map((r) => {
            const ms = toMs(r.expiryDate);
            const daysLeft = daysLeftFor(ms);
            return { ...r, ms, daysLeft, status: statusFor(daysLeft, r.warningDays) };
          })
          .sort((a, b) => a.ms - b.ms);
        const worst = entries.some((e) => e.status === 'expired') ? 'expired'
          : entries.some((e) => e.status === 'warning') ? 'warning'
            : entries.length ? 'ok' : null;
        const soonestMs = entries.length ? entries[0].ms : Infinity;
        return { product: p, entries, worst, soonestMs };
      });

    if (sortMode === 'alpha') {
      return withEntries.sort((a, b) => a.product.name.localeCompare(b.product.name));
    }
    const rank = { expired: 0, warning: 1, ok: 2, null: 3 };
    return withEntries.sort((a, b) => (rank[a.worst] - rank[b.worst]) || (a.soonestMs - b.soonestMs) || a.product.name.localeCompare(b.product.name));
  }, [products, recordsByProduct, query, groupFilter, sortMode]);

  const refreshStock = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${TEST_WORKER_URL}/warehouse/sync`, {
        method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: '{}',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.error) throw new Error(body.message || body.error || 'Не удалось обновить');
      // The worker writes productExpiries/_trackedProducts as part of the same
      // sync; useCollection's live subscription picks it up automatically.
    } catch (e) {
      setError(e.message || 'Не удалось обновить остатки');
    } finally {
      setRefreshing(false);
    }
  };

  const cancelEntry = async (id) => {
    setBusyId(id);
    setError(null);
    try {
      await sendTestWorkerCommand({
        user, path: '/commands/delete-product-expiry', body: { id },
        idempotencyKey: `emp:${user.uid}:cancel-expiry:${id}:${Date.now()}`,
      });
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (collection.loading && !collection.docs.length) return <Loader />;

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <ErrorBanner message={error || collection.error} />
        <View style={styles.head}>
          <SectionLabel>Контроль сроков годности</SectionLabel>
          <TextButton title={refreshing ? 'Обновляем…' : '↻ Обновить остатки'} onPress={refreshStock} />
        </View>
        <Text style={styles.hint}>
          Список товаров обновляется сам каждые ~3 часа. Приняли новый товар — нажмите «Обновить остатки», чтобы он сразу появился здесь.
        </Text>
        <Field label="Поиск" value={query} onChangeText={setQuery} placeholder="Название товара" />

        {groups.length > 1 && (
          <View style={styles.chipWrap}>
            <Pressable onPress={() => setGroupFilter(ALL_GROUPS)} style={[styles.chip, groupFilter === ALL_GROUPS && styles.chipOn]}>
              <Text style={[styles.chipText, groupFilter === ALL_GROUPS && styles.chipTextOn]}>Все группы</Text>
            </Pressable>
            {groups.map((g) => (
              <Pressable key={g} onPress={() => setGroupFilter(g)} style={[styles.chip, groupFilter === g && styles.chipOn]}>
                <Text style={[styles.chipText, groupFilter === g && styles.chipTextOn]}>{leafOf(g)}</Text>
              </Pressable>
            ))}
          </View>
        )}

        <View style={styles.sortRow}>
          <Text style={styles.sortLabel}>Сортировка:</Text>
          <Pressable onPress={() => setSortMode('urgency')} style={[styles.sortBtn, sortMode === 'urgency' && styles.sortBtnOn]}>
            <Text style={[styles.sortBtnText, sortMode === 'urgency' && styles.sortBtnTextOn]}>По сроку</Text>
          </Pressable>
          <Pressable onPress={() => setSortMode('alpha')} style={[styles.sortBtn, sortMode === 'alpha' && styles.sortBtnOn]}>
            <Text style={[styles.sortBtnText, sortMode === 'alpha' && styles.sortBtnTextOn]}>По алфавиту</Text>
          </Pressable>
        </View>

        {!trackedDoc && (
          <Text style={styles.hint}>Список товаров ещё не синхронизирован с МоимСкладом — обновится в течение нескольких минут.</Text>
        )}
        {trackedDoc && !rows.length && <Text style={styles.hint}>Товары не найдены.</Text>}

        {rows.map(({ product, entries }) => (
          <Card key={product.id} style={styles.card}>
            <View style={styles.head}>
              <Text style={styles.name}>{product.name}</Text>
              <Text style={styles.stock}>Остаток: {product.stock ?? 0}</Text>
            </View>
            {entries.map((e) => (
              <View key={e.id} style={styles.entryRow}>
                <Pill kind={STATUS_PILL[e.status].kind} label={STATUS_PILL[e.status].label} />
                <Text style={styles.entryDate}>годен до {ymd(e.ms)}</Text>
                <Pressable onPress={() => cancelEntry(e.id)} disabled={busyId === e.id} hitSlop={8}>
                  <Text style={styles.remove}>✕</Text>
                </Pressable>
              </View>
            ))}
            <TextButton title="+ Добавить срок" onPress={() => setModalProduct(product)} />
          </Card>
        ))}
      </ScrollView>

      {modalProduct && (
        <AddExpirySheet
          product={modalProduct}
          onClose={() => setModalProduct(null)}
          onSubmit={async ({ expiryDate, warningDays }) => {
            await sendTestWorkerCommand({
              user, path: '/commands/create-product-expiry',
              body: { productId: modalProduct.id, productName: modalProduct.name, expiryDate, warningDays },
              idempotencyKey: `emp:${user.uid}:create-expiry:${modalProduct.id}:${expiryDate}`,
            });
          }}
        />
      )}
    </View>
  );
}

function AddExpirySheet({ product, onClose, onSubmit }) {
  const [date, setDate] = useState('');
  const [warningDays, setWarningDays] = useState(String(EXPIRY_WARNING_DAYS * 3));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!DATE_RE.test(date.trim())) { setError('Дата в формате ГГГГ-ММ-ДД'); return; }
    const parsedWarning = parseInt(warningDays, 10);
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ expiryDate: date.trim(), warningDays: Number.isFinite(parsedWarning) ? parsedWarning : 30 });
      onClose();
    } catch (e) {
      setError(e?.code === 'ALREADY_EXISTS' ? 'Этот срок уже добавлен' : (e?.code ? `Отклонено: ${e.code}` : 'Не удалось добавить'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.wrap}>
        <View style={styles.sheet}>
          <View style={styles.sheetHead}>
            <Text style={styles.sheetTitle}>{product.name}</Text>
            <TextButton title="Отмена" kind="muted" onPress={onClose} />
          </View>
          <ErrorBanner message={error} />
          <Field label="Дата годности (ГГГГ-ММ-ДД)" value={date} onChangeText={setDate} placeholder="2026-12-31" autoCapitalize="none" />
          <Field label="За сколько дней предупреждать в приложении" value={warningDays} onChangeText={setWarningDays} keyboardType="number-pad" />
          <PrimaryButton title="Добавить" onPress={submit} loading={busy} />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  hint: { ...typography.caption, color: colors.textMuted, marginTop: spacing.sm },
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  chip: { backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: 6 },
  chipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,0,0,0.04)' },
  chipText: { ...typography.caption, color: colors.textPrimary },
  chipTextOn: { color: colors.primary, fontWeight: '700' },
  sortRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  sortLabel: { ...typography.caption, color: colors.textMuted },
  sortBtn: { borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.sm, paddingVertical: 4 },
  sortBtnOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,0,0,0.04)' },
  sortBtnText: { ...typography.caption, color: colors.textSecondary },
  sortBtnTextOn: { color: colors.primary, fontWeight: '700' },
  card: { gap: spacing.sm },
  head: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14, flex: 1 },
  stock: { ...typography.caption, color: colors.textMuted },
  entryRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  entryDate: { ...typography.caption, color: colors.textPrimary, flex: 1 },
  remove: { ...typography.body, color: colors.textMuted, fontSize: 16 },
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, gap: spacing.md, maxHeight: '90%' },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sheetTitle: { ...typography.h2, color: colors.textPrimary, flex: 1 },
});
